import React, { useState, useEffect, useRef } from "react";
import "./Login.css";
import Header from "../Header/Header";
import { useLocation, Navigate } from "react-router-dom";
import axiosConfig from "../../Service/AxiosConfig";
import { useAuth } from "../../Context/AuthContext";
import SuccessModal from "../../Modal/SuccessModal";
import Select from "react-select";

const Login = () => {
  const location = useLocation();
  const { login, user, isLoggedIn } = useAuth();
  const mobileno = location.state?.mobileno || "";
  const verifyBtnRef = useRef(null);

  const [serviceData, setServiceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const otpInputRef = useRef(null);
  const [timer, setTimer] = useState(0);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [providerFetched, setProviderFetched] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [formData, setFormData] = useState({
    mobileno,
    otp: "",
    services: "",
    name: "",
    gender: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (timer <= 0) return;
    const interval = setInterval(() => {
      setTimer((t) => t - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [timer]);
  useEffect(() => {
    if (otpSent && !otpVerified) {
      setTimeout(() => {
        otpInputRef.current?.focus();
      }, 100);
    }
  }, [otpSent, otpVerified]);
  useEffect(() => {
    if (formData.otp.length === 4) {
      verifyBtnRef.current?.focus();
    }
  }, [formData.otp]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await axiosConfig.get(
          "/catlog/services/?is_suspended=false",
        );
        setServiceData(res?.data?.results || []);
      } catch (err) {
        console.error("Service fetch failed", err);
      }
    };
    fetchServices();
  }, []);
  useEffect(() => {
    if (!otpVerified) return;
    if (!user?.id) return;
    if (providerFetched) return;

    fetchProviderData(user.id);
    setProviderFetched(true);
  }, [otpVerified, user, providerFetched]);
  useEffect(() => {
    if (isLoggedIn && user?.id) {
      setOtpVerified(true);
      setOtpSent(true);
    }
  }, [isLoggedIn, user]);

  if (!mobileno) return <Navigate to="/" replace />;
  if (isLoggedIn && user?.profile_completed)
    return <Navigate to="/user-profile" replace />;

  const fetchProviderData = async (userId) => {
    if (!userId) return;
    try {
      const res = await axiosConfig.get(
        `/provider-data/providers/?user=${userId}`,
      );

      const provider = res?.data?.results?.[0];
      if (!provider) return;

      setFormData((prev) => ({
        ...prev,
        id: provider.id,
        services: provider.services?.[0]?.id || "",
        name: provider?.user?.name ?? "",
        gender: provider.gender ?? "",
      }));
    } catch (error) {
      console.log("No existing provider profile", error);
      setErrors({
        mobileno:
          error?.response?.data?.error ||
          error?.response?.data?.detail ||
          "Failed to fetch provider data",
      });
    }
  };

  const serviceOptions = serviceData.map((s) => ({
    value: s.id,
    label: s.name,
  }));

  const genderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Other", label: "Other" },
  ];

  const sendOtp = async () => {
    setLoading(true);
    setErrors({});
    try {
      const payload = new FormData();
      payload.append("mobileno", mobileno);
      payload.append("role", "carepreneur");
      await axiosConfig.post("/accounts/validate_mobile/", payload);
      setOtpSent(true);
      setTimer(30);
      setFormData((prev) => ({ ...prev, otp: "" }));
    } catch (err) {
      setErrors({
        otp:
          err?.response?.data?.error ||
          err?.response?.data?.detail ||
          "Failed to send OTP",
      });
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (formData.otp.length !== 4) return;
    setLoading(true);
    setErrors({});
    try {
      const payload = new FormData();
      payload.append("mobileno", mobileno);
      payload.append("otp", formData.otp);
      const res = await axiosConfig.post("/accounts/verify_otp/", payload);
      const loggedInUser = {
        id: res.data.user_id,
        mobileno: res.data.mobileno,
      };

      login(res.data.token, loggedInUser);
      setOtpVerified(true);
    } catch (err) {
      setErrors({
        otp:
          err?.response?.data?.error ||
          err?.response?.data?.detail ||
          "OTP verification failed",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!formData.services)
      return setErrors({ services: "Service is required" });
    if (!formData.name) return setErrors({ name: "Full name is required" });
    if (!formData.gender) return setErrors({ gender: "Gender is required" });
    if (!user?.id) return;

    setLoading(true);
    try {
      await axiosConfig.patch(`/provider-data/providers/${formData?.id}/`, {
        services: formData.services,
        name: formData.name,
        gender: formData.gender,
        mobileno: formData.mobileno,
      });
      setShowSuccessModal(true);
    } catch (err) {
      console.error("Profile submit failed", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />

      <div className="login-container">
        <h2 className="step-title">Let’s Get Started!</h2>
        <div className="form-group">
          <label>Mobile Number</label>
          <input value={`+91 ${mobileno}`} disabled className="form-control" />
          {errors.mobileno && <p className="err-msg">{errors?.mobileno}</p>}
        </div>
        {!otpSent && (
          <button className="submit-btn" onClick={sendOtp} disabled={loading}>
            Get OTP
          </button>
        )}
        {otpSent && !otpVerified && (
          <>
            <div className="form-group">
              <label>Enter 4-digit OTP</label>
              <input
                ref={otpInputRef}
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                placeholder="****"
                className="form-control"
                value={formData.otp}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    otp: e.target.value.replace(/\D/g, ""),
                  });
                  setErrors({});
                }}
              />
              {errors.otp && <p className="err-msg">{errors.otp}</p>}
            </div>

            {formData.otp.length === 4 && (
              <button
                className="submit-btn"
                onClick={verifyOtp}
                ref={verifyBtnRef}
                disabled={loading}
              >
                Verify OTP
              </button>
            )}

            <button
              className="link-btn"
              onClick={sendOtp}
              disabled={loading || timer > 0}
            >
              {timer > 0 ? `Resend OTP in ${timer}s` : "Resend OTP"}
            </button>
          </>
        )}
        {otpVerified && (
          <>
            <p className="success-text">✅ OTP Verified Successfully</p>
            <div className="form-group">
              <label>Service*</label>
              <Select
                name="services"
                options={serviceOptions}
                value={serviceOptions.find(
                  (o) => o.value === formData.services,
                )}
                onChange={(opt) =>
                  setFormData({ ...formData, services: opt?.value || "" })
                }
              />
              {errors.services && <p className="err-msg">{errors.services}</p>}
            </div>

            <div className="form-group">
              <label>Full Name*</label>
              <input
                name="name"
                placeholder="Enter Full Name"
                type="text"
                className="form-control"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
              {errors.name && <p className="err-msg">{errors.name}</p>}
            </div>

            <div className="form-group">
              <label>Gender*</label>
              <Select
                name="gender"
                options={genderOptions}
                value={genderOptions.find((o) => o.value === formData.gender)}
                onChange={(opt) =>
                  setFormData({ ...formData, gender: opt?.value || "" })
                }
              />
              {errors.gender && <p className="err-msg">{errors.gender}</p>}
            </div>

            <button
              className="submit-btn"
              onClick={handleSubmit}
              disabled={loading}
            >
              Submit
            </button>
          </>
        )}
      </div>

      <SuccessModal
        show={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
      />
    </>
  );
};

export default Login;
